<template>
  <div class="editor-texto" :class="{ 'is-disabled': disabled }">
    <div class="editor-texto__toolbar">
      <button
        class="editor-texto__btn"
        type="button"
        :disabled="disabled || !canUndo"
        @mousedown.prevent="saveSelection"
        @click="undo"
        title="Deshacer"
      >
        <span>↶</span>
      </button>
      <button
        class="editor-texto__btn"
        type="button"
        :disabled="disabled || !canRedo"
        @mousedown.prevent="saveSelection"
        @click="redo"
        title="Rehacer"
      >
        <span>↷</span>
      </button>

      <span class="editor-texto__sep"></span>

      <button
        class="editor-texto__btn"
        type="button"
        :class="{ 'is-active': toolbarState.bold }"
        :disabled="disabled"
        @mousedown.prevent="saveSelection"
        @click="cmd('bold')"
        title="Negrita"
      >
        <strong>B</strong>
      </button>
      <button
        class="editor-texto__btn"
        type="button"
        :class="{ 'is-active': toolbarState.italic }"
        :disabled="disabled"
        @mousedown.prevent="saveSelection"
        @click="cmd('italic')"
        title="Cursiva"
      >
        <em>I</em>
      </button>
      <button
        class="editor-texto__btn"
        type="button"
        :class="{ 'is-active': toolbarState.underline }"
        :disabled="disabled"
        @mousedown.prevent="saveSelection"
        @click="cmd('underline')"
        title="Subrayado"
      >
        <span style="text-decoration: underline;">U</span>
      </button>

      <span class="editor-texto__sep"></span>

      <button
        class="editor-texto__btn"
        type="button"
        :class="{ 'is-active': toolbarState.ul }"
        :disabled="disabled"
        @mousedown.prevent="saveSelection"
        @click="cmd('insertUnorderedList')"
        title="Lista"
      >
        <span>•</span>
      </button>
      <button
        class="editor-texto__btn"
        type="button"
        :class="{ 'is-active': toolbarState.ol }"
        :disabled="disabled"
        @mousedown.prevent="saveSelection"
        @click="cmd('insertOrderedList')"
        title="Lista numerada"
      >
        <span>1.</span>
      </button>

      <span class="editor-texto__sep"></span>

      <button class="editor-texto__btn" type="button" :disabled="disabled" @mousedown.prevent="saveSelection" @click="createLink" title="Enlace">
        <span>🔗</span>
      </button>

      <span class="editor-texto__sep"></span>

      <button
        class="editor-texto__btn"
        type="button"
        :disabled="disabled"
        @mousedown.prevent="saveSelection"
        @click="clearFormatting"
        title="Limpiar formato"
      >
        <span>Tx</span>
      </button>
    </div>

    <div
      ref="editableRef"
      class="editor-texto__editable"
      :data-placeholder="placeholder"
      :contenteditable="!disabled"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
      @mouseup="saveSelection"
      @keyup="saveSelection"
    ></div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "Escribe aquí..." },
  height: { type: [Number, String], default: 300 },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const editableRef = ref(null);
const isFocused = ref(false);
const savedRange = ref(null);
const toolbarState = reactive({
  bold: false,
  italic: false,
  underline: false,
  ul: false,
  ol: false,
});

// Historial propio (más confiable que execCommand('undo/redo'))
const history = ref([]);
const historyIndex = ref(-1);
const isApplyingHistory = ref(false);

const minHeight = computed(() => {
  const h = typeof props.height === "number" ? `${props.height}px` : props.height;
  return h || "300px";
});

function normalizeHtml(html) {
  if (!html) return "";
  const cleaned = String(html)
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  // Si después de quitar br/nbsp queda vacío o solo tags vacíos, lo tratamos como vacío
  const textOnly = cleaned.replace(/<[^>]*>/g, "").trim();
  return textOnly ? String(html) : "";
}

function getCurrentHtml() {
  const el = editableRef.value;
  if (!el) return "";
  return normalizeHtml(el.innerHTML);
}

function setHtml(html) {
  const el = editableRef.value;
  if (!el) return;
  el.innerHTML = html || "";
}

function syncFromModel() {
  // No pisar mientras el usuario está escribiendo
  if (isFocused.value) return;
  const current = getCurrentHtml();
  const incoming = normalizeHtml(props.modelValue);
  if (current !== incoming) setHtml(incoming);
}

function emitModel() {
  emit("update:modelValue", getCurrentHtml());
}

function moveCaretToEnd() {
  const el = editableRef.value;
  if (!el) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection?.();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}

function pushHistory(html) {
  const normalized = normalizeHtml(html);
  const current = history.value[historyIndex.value];
  if (current === normalized) return;

  // corta "redo stack" si el usuario escribe tras undo
  if (historyIndex.value < history.value.length - 1) {
    history.value = history.value.slice(0, historyIndex.value + 1);
  }
  history.value.push(normalized);
  historyIndex.value = history.value.length - 1;
}

const canUndo = computed(() => historyIndex.value > 0);
const canRedo = computed(() => historyIndex.value >= 0 && historyIndex.value < history.value.length - 1);

function applyHistoryAt(index) {
  const target = history.value[index];
  if (typeof target !== "string") return;
  isApplyingHistory.value = true;
  setHtml(target);
  historyIndex.value = index;
  nextTick(() => {
    moveCaretToEnd();
    emitModel();
    isApplyingHistory.value = false;
    updateToolbarState();
  });
}

function undo() {
  if (props.disabled) return;
  if (!canUndo.value) return;
  focusEditor();
  applyHistoryAt(historyIndex.value - 1);
}

function redo() {
  if (props.disabled) return;
  if (!canRedo.value) return;
  focusEditor();
  applyHistoryAt(historyIndex.value + 1);
}

function focusEditor() {
  const el = editableRef.value;
  if (!el) return;
  el.focus();
}

function isRangeInsideEditor(range) {
  const el = editableRef.value;
  if (!el || !range) return false;
  const node = range.commonAncestorContainer;
  return el === node || el.contains(node);
}

function saveSelection() {
  if (props.disabled) return;
  const sel = window.getSelection?.();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  if (!isRangeInsideEditor(range)) return;
  // Clonamos para que sobreviva a cambios de foco
  savedRange.value = range.cloneRange();
}

function restoreSelection() {
  if (props.disabled) return false;
  const sel = window.getSelection?.();
  if (!sel) return false;
  const range = savedRange.value;
  if (!range || !isRangeInsideEditor(range)) return false;
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}

function cmd(command, value = null) {
  if (props.disabled) return;
  focusEditor();
  restoreSelection();
  // document.execCommand sigue siendo lo más compatible para un editor simple.
  document.execCommand(command, false, value);
  nextTick(() => {
    const html = getCurrentHtml();
    emitModel();
    pushHistory(html);
    updateToolbarState();
    saveSelection();
  });
}

function createLink() {
  if (props.disabled) return;
  const url = window.prompt("URL del enlace:", "https://");
  if (!url) return;

  focusEditor();
  restoreSelection();
  const selection = window.getSelection();
  const hasSelection = selection && selection.rangeCount > 0 && String(selection.toString()).length > 0;

  if (hasSelection) {
    document.execCommand("createLink", false, url);
  } else {
    // Si no hay selección, inserta el enlace como texto
    document.execCommand("insertHTML", false, `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
  }

  nextTick(() => emitModel());
}

function stripFormatting(rootEl) {
  const UNWRAP_TAGS = new Set([
    "B",
    "STRONG",
    "I",
    "EM",
    "U",
    "SPAN",
    "FONT",
    "MARK",
    "SMALL",
    "BIG",
    "CODE",
    "A",
  ]);

  const unwrap = (el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  };

  const cleanAttrs = (el) => {
    // elimina atributos para evitar estilos inline pegados desde Word/Correo
    const attrs = Array.from(el.attributes || []);
    for (const a of attrs) el.removeAttribute(a.name);
  };

  const walk = (node) => {
    const children = Array.from(node.childNodes || []);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        walk(child);
        const tag = child.tagName;
        if (UNWRAP_TAGS.has(tag)) {
          unwrap(child);
        } else {
          cleanAttrs(child);
        }
      }
    }
  };

  walk(rootEl);
}

function clearFormatting() {
  if (props.disabled) return;
  focusEditor();
  restoreSelection();

  const el = editableRef.value;
  if (!el) return;

  const sel = window.getSelection?.();
  const hasSelection = sel && sel.rangeCount > 0 && String(sel.toString()).length > 0;

  if (hasSelection) {
    // Reemplaza SOLO la selección por texto plano (sin HTML)
    const range = sel.getRangeAt(0);
    if (!isRangeInsideEditor(range)) return;
    const text = sel.toString();
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    // recolocar cursor al final de lo insertado
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.setStart(range.endContainer, range.endOffset);
    newRange.collapse(true);
    sel.addRange(newRange);
  } else {
    // Si no hay selección: limpia formato en TODO el editor pero mantiene estructura (p/ul/ol/li)
    stripFormatting(el);
  }

  nextTick(() => {
    const html = getCurrentHtml();
    setHtml(html);
    emitModel();
    pushHistory(html);
    updateToolbarState();
    saveSelection();
  });
}

function onInput() {
  if (isApplyingHistory.value) return;
  const html = getCurrentHtml();
  emitModel();
  pushHistory(html);
  updateToolbarState();
}

function onFocus() {
  isFocused.value = true;
  nextTick(() => saveSelection());
}

function onBlur() {
  isFocused.value = false;
  // Normaliza vacío para que el placeholder funcione bien
  const html = getCurrentHtml();
  setHtml(html);
  emitModel();
  pushHistory(html);
  updateToolbarState();
}

function updateToolbarState() {
  if (props.disabled) return;
  try {
    toolbarState.bold = !!document.queryCommandState("bold");
    toolbarState.italic = !!document.queryCommandState("italic");
    toolbarState.underline = !!document.queryCommandState("underline");
    toolbarState.ul = !!document.queryCommandState("insertUnorderedList");
    toolbarState.ol = !!document.queryCommandState("insertOrderedList");
  } catch (e) {
    // ignore
  }
}

onMounted(() => {
  const initial = normalizeHtml(props.modelValue);
  setHtml(initial);
  const el = editableRef.value;
  if (el) el.style.minHeight = minHeight.value;
  pushHistory(initial);
  updateToolbarState();

  const onSelectionChange = () => {
    // Mantiene savedRange actualizado cuando el usuario selecciona texto
    saveSelection();
    updateToolbarState();
  };
  document.addEventListener("selectionchange", onSelectionChange);
  // guardamos para cleanup
  editableRef.value && (editableRef.value.__selectionChangeHandler = onSelectionChange);
});

onBeforeUnmount(() => {
  const handler = editableRef.value?.__selectionChangeHandler;
  if (handler) document.removeEventListener("selectionchange", handler);
});

watch(minHeight, (h) => {
  const el = editableRef.value;
  if (el) el.style.minHeight = h;
});

watch(
  () => props.modelValue,
  () => {
    syncFromModel();
    // si llega un valor externo (y no estamos editando), también lo metemos al historial
    if (!isFocused.value && !isApplyingHistory.value) {
      pushHistory(getCurrentHtml());
      updateToolbarState();
    }
  }
);
</script>

<style scoped>
.editor-texto {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.editor-texto__toolbar {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-wrap: wrap;
}

.editor-texto__select {
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  font-size: 13px;
}

.editor-texto__btn {
  height: 32px;
  min-width: 32px;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #111827;
}

.editor-texto__btn.is-active {
  background: #e0e7ff;
  border-color: #a5b4fc;
}

.editor-texto__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.editor-texto__sep {
  width: 1px;
  height: 22px;
  background: #e5e7eb;
  margin: 0 4px;
}

.editor-texto__editable {
  padding: 12px;
  outline: none;
  font-family: "Plus Jakarta Sans", Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
}

.editor-texto__editable :deep(ul),
.editor-texto__editable :deep(ol) {
  margin: 0.25rem 0;
  padding-left: 1.25rem;
  list-style-position: inside;
}

.editor-texto__editable :deep(li) {
  margin: 0.15rem 0;
}

.editor-texto__editable:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
}

.is-disabled .editor-texto__editable {
  background: #f9fafb;
}
</style>
